<template>
  <!-- A side work finding: in the list of the daily check and as evidence of a chat answer. -->
  <article
    class="ai-finding"
    :class="[`is-${data.level || 'low'}`, { 'is-focused': focused }]"
    :data-eid="evidenceId"
  >
    <header class="ai-finding__head">
      <span
        v-if="number"
        class="ai-evidence-number"
      >
        {{ number }}
      </span>
      <span
        class="ai-finding__badge ai-tone"
        :class="tone"
      >
        <ai-icon
          name="flag"
          :size="13"
        />
        Возможный сторонний проект · {{ data.levelLabel || 'уровень не указан' }}
      </span>
      <span
        class="ai-finding__score"
        title="Сумма баллов сработавших правил"
      >
        {{ scoreLabel }}
      </span>
      <span
        v-if="statusLabel"
        class="ai-finding__status"
      >
        {{ statusLabel }}
      </span>
      <span class="ai-finding__time">{{ period }}</span>
    </header>

    <div
      v-if="data.computer"
      class="ai-finding__line is-muted"
    >
      <ai-icon
        name="computer"
        :size="14"
      />
      <span>{{ data.computer }}</span>
    </div>
    <div class="ai-finding__line ai-finding__file">
      <ai-icon
        name="file"
        :size="14"
      />
      <span>{{ data.file || 'Файл без имени' }}</span>
    </div>
    <div
      v-if="data.path"
      class="ai-finding__path"
    >
      {{ data.path }}
    </div>

    <ul class="ai-finding__facts">
      <li :class="{ 'is-missing': !data.project }">
        <ai-icon
          name="project"
          :size="14"
        />
        <span>{{ projectLabel }}</span>
      </li>
      <li v-if="data.qualityLabel">
        <ai-icon
          :name="data.quality === 'path' ? 'check' : 'info'"
          :size="14"
        />
        <span>{{ data.qualityLabel }}</span>
      </li>
      <li v-if="data.activeTime">
        <ai-icon
          name="clock"
          :size="14"
        />
        <span>работа {{ data.activeTime }}</span>
      </li>
    </ul>

    <ol
      v-if="reasons.length"
      class="ai-finding__reasons"
    >
      <li
        v-for="(reason, index) in reasons"
        :key="index"
      >
        <span class="ai-finding__rule">{{ reason.rule }} +{{ reason.points }}</span>
        <span class="ai-finding__reason">
          {{ reason.text }}
          <span
            v-if="reason.refs && reason.refs.length"
            class="ai-finding__refs"
          >
            <template v-for="(ref, refIndex) in reason.refs">
              <button
                v-if="inChat && ref.evidence"
                :key="refIndex"
                type="button"
                class="ai-finding__ref"
                title="Показать запись"
                @click="$emit('open', ref.evidence)"
              >
                {{ refLabel(ref) }}
              </button>
              <router-link
                v-else-if="journal(ref)"
                :key="refIndex"
                :to="journal(ref)"
                target="_blank"
                class="ai-finding__ref"
                title="Открыть в журнале"
              >
                {{ refLabel(ref) }}
              </router-link>
              <span
                v-else
                :key="refIndex"
                class="ai-finding__ref is-plain"
              >
                {{ refLabel(ref) }}
              </span>
            </template>
          </span>
        </span>
      </li>
    </ol>

    <div
      v-if="transfers.length"
      class="ai-finding__block"
    >
      <span class="ai-finding__label">Передачи файла</span>
      <ul>
        <li
          v-for="(transfer, index) in transfers"
          :key="index"
        >
          <span class="ai-finding__when">{{ clock(transfer.time) }}</span>
          {{ transfer.channel || 'канал не указан' }}
          <template v-if="transfer.recipients && transfer.recipients.length">
            → {{ transfer.recipients.join(', ') }}
          </template>
          <template v-if="transfer.name && transfer.name !== data.file">
            · «{{ transfer.name }}»
          </template>
          <button
            v-if="inChat && transfer.evidence"
            type="button"
            class="ai-finding__ref"
            title="Показать запись"
            @click="$emit('open', transfer.evidence)"
          >
            запись
          </button>
        </li>
      </ul>
    </div>

    <div
      v-if="work.length"
      class="ai-finding__block"
    >
      <button
        type="button"
        class="ai-link-button"
        :aria-expanded="showWork ? 'true' : 'false'"
        @click="showWork = !showWork"
      >
        {{ showWork ? 'Скрыть работу с файлом' : `Работа с файлом (${work.length})` }}
      </button>
      <ul v-if="showWork">
        <li
          v-for="(entry, index) in work"
          :key="index"
        >
          <span class="ai-finding__when">{{ clock(entry.time) }}</span>
          {{ entry.caption || 'окно программы' }}
          <template v-if="entry.seconds">
            · {{ minutes(entry.seconds) }}
          </template>
        </li>
      </ul>
    </div>

    <footer
      v-if="actions.length"
      class="ai-finding__actions"
    >
      <button
        v-for="action in actions"
        :key="action.status"
        type="button"
        class="ai-button is-small"
        :class="{ 'is-ghost': action.ghost }"
        :disabled="busy"
        @click="$emit('status', action.status)"
      >
        <ai-icon
          :name="action.icon"
          :size="14"
        />
        {{ action.label }}
      </button>
    </footer>
  </article>
</template>

<script>
  import AiIcon from './AiIcon.vue';
  import { count, formatTime } from './format';
  import { recordLink } from './links';

  const TONES = { high: 'is-danger', medium: 'is-warning', low: 'is-neutral' };
  const STATUS_LABELS = { new: 'новая', seen: 'просмотрена', dismissed: 'отклонена' };
  const ACTIONS = {
    new: [
      { status: 'seen', label: 'Просмотрено', icon: 'check' },
      { status: 'dismissed', label: 'Отклонить', icon: 'close', ghost: true },
    ],
    seen: [
      { status: 'dismissed', label: 'Отклонить', icon: 'close', ghost: true },
      { status: 'new', label: 'Вернуть в новые', icon: 'reset', ghost: true },
    ],
    dismissed: [{ status: 'new', label: 'Вернуть в новые', icon: 'reset', ghost: true }],
  };

  /** A finding from its chat evidence: the card keeps the fields the list item has at the top level. */
  function fromEvidence(item) {
    const card = (item && item.card) || {};
    const field = (card.fields || []).find((entry) => entry.label === 'Время работы');
    return {
      ...(card.finding || {}),
      file: card.title,
      computer: item.computerName || card.actor,
      activeTime: field ? field.value : null,
    };
  }

  export default {
    name: 'FindingCard',
    components: { AiIcon },
    props: {
      // A finding of GET /sidework/findings, or a chat evidence item of category "finding"
      // (then references to evidence open it in the panel and there are no review buttons).
      finding: { type: Object, default: null },
      item: { type: Object, default: null },
      number: { type: [Number, String], default: null },
      focused: { type: Boolean, default: false },
      showStatus: { type: Boolean, default: false },
      busy: { type: Boolean, default: false },
    },
    data: () => ({ showWork: false }),
    computed: {
      data() {
        return this.finding || fromEvidence(this.item);
      },
      inChat() {
        return !this.finding && !!this.item;
      },
      evidenceId() {
        return this.item ? this.item.id : null;
      },
      tone() {
        return TONES[this.data.level] || TONES.low;
      },
      scoreLabel() {
        return count(Number(this.data.score) || 0, ['балл', 'балла', 'баллов']);
      },
      statusLabel() {
        return this.showStatus ? STATUS_LABELS[this.data.status] || '' : '';
      },
      period() {
        const { first, last } = this.data;
        if (!first) return '';
        if (!last || last === first) return formatTime(first);
        if (String(first).slice(0, 10) === String(last).slice(0, 10)) return `${formatTime(first)}–${this.clock(last)}`;
        return `${formatTime(first)} – ${formatTime(last)}`;
      },
      projectLabel() {
        const { project, projectBy } = this.data;
        if (!project) return 'проект не определён';
        const name = [project.code, project.name].filter(Boolean).join(' · ');
        const closed = project.status === 'closed' ? ' (закрыт)' : '';
        return `${name}${closed}${projectBy ? ` — ${projectBy}` : ''}`;
      },
      reasons() {
        return this.data.reasons || [];
      },
      transfers() {
        return this.data.transfers || [];
      },
      work() {
        return this.data.work || [];
      },
      actions() {
        return this.inChat ? [] : ACTIONS[this.data.status] || [];
      },
      sources() {
        return (this.$store.state.assistant.capabilities || {}).sources || [];
      },
    },
    methods: {
      clock(value) {
        const text = String(value || '');
        return /\d{2}:\d{2}/.test(text) ? text.slice(11, 16) : '';
      },
      minutes(seconds) {
        const total = Math.max(1, Math.round(seconds / 60));
        return total < 60 ? `${total} мин` : `${Math.floor(total / 60)} ч ${total % 60} мин`;
      },
      refLabel(ref) {
        const source = this.sources.find((item) => item.slug === ref.category);
        let label = source ? source.label : ref.category;
        if (ref.category === 'activity_feed') label = 'Лента активности';
        const time = this.clock(ref.time);
        return [label, time].filter(Boolean).join(' · ');
      },
      // The log page of the record's day; the activity feed has no record page.
      journal(ref) {
        const { category, computerId, recordId, time } = ref;
        return recordLink({ linkSource: category, computerId, recordId, time });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-finding {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 12px 12px 14px;
    border: 1px solid var(--ui-border);
    border-left: 3px solid var(--ui-border-strong);
    border-radius: var(--ui-radius);
    background: var(--ui-surface);
    box-shadow: var(--ui-shadow-sm);
    font-size: 13px;
    line-height: 1.45;
    scroll-margin: 12px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &.is-high {
      border-left-color: var(--ui-danger);
    }

    &.is-medium {
      border-left-color: var(--ui-warning);
    }

    &.is-focused {
      border-color: var(--ui-primary);
      box-shadow: var(--ui-focus);
    }
  }

  .ai-finding__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
    min-width: 0;
  }

  .ai-finding__badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;

    &.is-neutral {
      background: var(--ui-bg-strong);
      color: var(--ui-text);
    }
  }

  .ai-finding__score,
  .ai-finding__status {
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  .ai-finding__status {
    padding: 0 6px;
    border-radius: var(--ui-radius-sm);
    background: var(--ui-bg-strong);
  }

  .ai-finding__time {
    margin-left: auto;
    color: var(--ui-text-muted);
    font-size: 12px;
    white-space: nowrap;
  }

  .ai-finding__line {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    color: var(--ui-text);

    &.is-muted {
      color: var(--ui-text-muted);
    }

    span {
      overflow-wrap: anywhere;
    }
  }

  .ai-finding__file {
    font-weight: 500;
  }

  .ai-finding__path {
    margin-top: -3px;
    padding-left: 20px;
    color: var(--ui-text-muted);
    font-size: 12px;
    overflow-wrap: anywhere;
  }

  .ai-finding__facts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin: 0;
    padding: 0;
    color: var(--ui-text);
    list-style: none;

    li {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      min-width: 0;
    }

    .ai-icon {
      color: var(--ui-text-muted);
    }

    .is-missing {
      color: var(--ui-text-muted);
    }
  }

  .ai-finding__reasons {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 2px 0 0;
    padding: 8px 10px;
    border-radius: var(--ui-radius-sm);
    background: var(--ui-surface-soft);
    list-style: none;

    li {
      display: flex;
      gap: 8px;
      min-width: 0;
    }
  }

  .ai-finding__rule {
    flex: 0 0 auto;
    min-width: 44px;
    color: var(--ui-text-muted);
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    line-height: 19px;
    white-space: nowrap;
  }

  .ai-finding__reason {
    min-width: 0;
    color: var(--ui-text);
    overflow-wrap: anywhere;
  }

  .ai-finding__refs {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-left: 4px;
    vertical-align: 1px;
  }

  // A record behind a reason: opens the evidence in a chat, the log page in the list.
  .ai-finding__ref {
    display: inline-flex;
    align-items: center;
    padding: 0 7px;
    border-radius: 999px !important;
    background: var(--ui-primary-soft);
    color: var(--ui-primary);
    font-family: inherit;
    font-size: 11px;
    line-height: 18px;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
      background: var(--ui-primary);
      color: #fff;
      text-decoration: none;
    }

    &.is-plain {
      background: var(--ui-bg-strong);
      color: var(--ui-text-muted);
      cursor: default;
    }
  }

  .ai-finding__block {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;

    ul {
      display: flex;
      flex-direction: column;
      gap: 2px;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    li {
      color: var(--ui-text);
      overflow-wrap: anywhere;
    }
  }

  .ai-finding__label {
    color: var(--ui-text-muted);
    font-size: 12px;
    font-weight: 500;
  }

  .ai-finding__when {
    margin-right: 4px;
    color: var(--ui-text-muted);
    font-variant-numeric: tabular-nums;
  }

  .ai-finding__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding-top: 4px;
  }
</style>
